import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { CTAFooter } from "@/pages/Prestations";
import { useSeo, JsonLd } from "@/lib/seo";
import { api } from "@/lib/api";
import RentalRequestForm from "@/components/location/RentalRequestForm";

export const priceLine = (i) => (i.price_from ? `à partir de ${Math.round(i.price_from)} € TTC` : "sur devis");
const depAmount = (i) => i.deposit_display ?? i.deposit_amount;
export const depositLine = (i) => (depAmount(i) ? `Dépôt de garantie : ${i.deposit_varies ? "à partir de " : ""}${Math.round(depAmount(i))} €` : "Dépôt de garantie : montant communiqué lors du devis");
export const DEPOSIT_NOTE = "Le dépôt de garantie est distinct du prix de la location et est sécurisé par carte bancaire via Stripe. Il est géré conformément aux conditions prévues dans le contrat de location et n'est libéré qu'après contrôle du matériel par My Animation.";
export function DepositBlock({ i, testid, note = true }) {
  return (
    <div className="border-t border-white/10 pt-3 mt-3" data-testid={testid}>
      <p className="text-sm text-white/85" data-testid={testid && `${testid}-amount`}>{depositLine(i)}</p>
      {note && <p className="text-[11px] text-white/45 mt-1 leading-relaxed">{DEPOSIT_NOTE}</p>}
    </div>
  );
}
export const photoUrl = (u) => (!u ? "" : /^https?:\/\//.test(u) ? u : u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const FAQ = [
  { q: "Comment réserver du matériel en location ?", a: "Choisissez votre matériel, envoyez votre demande avec la date et l'adresse de l'événement. Nous vous adressons un devis ; la réservation est confirmée après signature électronique et versement d'un acompte de 30 %." },
  { q: "Comment fonctionne le dépôt de garantie ?", a: "Le dépôt de garantie est distinct du prix de la location. Il est garanti uniquement par carte bancaire via Stripe : votre carte est enregistrée de façon sécurisée, puis le montant est bloqué (sans être débité) environ 2 jours avant la remise. Il est libéré après restitution et contrôle du matériel." },
  { q: "Livraison ou retrait : comment ça se passe ?", a: "Selon le matériel, vous pouvez venir le retirer à Bousbecque ou demander une livraison avec installation dans le Nord, à Lille et dans les Hauts-de-France. Les frais de livraison éventuels sont précisés sur le devis." },
  { q: "Que se passe-t-il en cas de casse ou de matériel manquant ?", a: "Un état des lieux est réalisé à la remise et au retour. En cas de dégât, de perte ou d'élément manquant, les frais de remise en état ou de remplacement peuvent être retenus sur le dépôt de garantie, conformément au contrat de location." },
];

export function ItemCard({ i }) {
  const photo = i.photos?.[0]?.url;
  return (
    <Link to={`/location/${i.slug}`} data-testid={`loc-card-${i.slug}`} className="card-luxe group flex flex-col overflow-hidden reveal">
      <div className="aspect-[4/3] bg-[#0d0d0d] overflow-hidden border-b border-[#DCB457]/15">
        {photo ? <img src={photoUrl(photo)} alt={i.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
          : <div className="w-full h-full flex items-center justify-center font-serif-luxe text-3xl text-[#DCB457]/40">{i.name}</div>}
      </div>
      <div className="p-6 flex flex-col flex-1">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#DCB457]">{i.category?.name}</p>
        <h3 className="font-serif-luxe text-2xl mt-2">{i.name}</h3>
        <p className="text-white/60 text-sm mt-3 leading-relaxed flex-1">{i.short_description}</p>
        <p className="gold-text font-serif-luxe text-xl mt-5">Location : {priceLine(i)}</p>
        <DepositBlock i={i} note={false} testid={`loc-card-deposit-${i.slug}`} />
        <span className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#DCB457] group-hover:gap-3 transition-all">Voir la fiche <ArrowRight size={13} /></span>
      </div>
    </Link>
  );
}

export default function Location() {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("all");
  useSeo({
    title: "Location matériel événementiel Nord & Lille | My Animation",
    description: "Location de matériel événementiel dans le Nord, à Lille et en Hauts-de-France : photobooth, livre d'or audio, livre d'or vidéo, sonorisation, éclairage. Devis rapide, retrait ou livraison.",
    path: "/location",
  });
  useEffect(() => { api.get("/location/catalog").then((r) => setData(r.data)).catch(() => setData({ categories: [], items: [] })); }, []);
  const cats = useMemo(() => (data?.categories || []).filter((c) => data.items.some((i) => i.category_id === c.id)), [data]);
  const items = (data?.items || []).filter((i) => cat === "all" || i.category_id === cat);

  return (
    <>
      <JsonLd data={{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) }} />
      {data?.items?.length > 0 && (
        <JsonLd data={{ "@context": "https://schema.org", "@type": "ItemList", name: "Location de matériel My Animation",
          itemListElement: data.items.map((i, n) => ({ "@type": "ListItem", position: n + 1, url: `https://myanimation.fr/location/${i.slug}`, name: i.name })) }} />
      )}
      <PageHero eyebrow="Location de matériel" img="/assets/entreprises/entreprise-09.jpg"
        title={<>Le matériel de vos <span className="italic gold-text">plus beaux moments.</span></>}
        subtitle="Photobooth, livres d'or audio et vidéo, sonorisation, éclairage : le matériel My Animation à la location dans le Nord, à Lille et en Hauts-de-France — préparé, testé et expliqué." />

      <section className="py-20 md:py-28 px-6 md:px-10 lg:px-14 bg-[#080808] border-b border-[#DCB457]/15" data-testid="loc-catalog">
        <div className="max-w-[1400px] mx-auto">
          <span className="eyebrow">Catalogue</span>
          <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3 leading-[1.05]">Choisissez votre <span className="italic gold-text">matériel.</span></h2>
          {cats.length > 1 && (
            <div className="flex flex-wrap gap-2 mt-8" data-testid="loc-cat-filter">
              {[{ id: "all", name: "Tout" }, ...cats].map((c) => (
                <button key={c.id} onClick={() => setCat(c.id)} data-testid={`loc-cat-${c.slug || "all"}`}
                  className={`text-[11px] uppercase tracking-[0.18em] px-4 py-2 border transition-colors ${cat === c.id ? "border-[#DCB457] text-[#DCB457] bg-[#DCB457]/10" : "border-white/12 text-white/60 hover:border-[#DCB457]/40"}`}>{c.name}</button>
              ))}
            </div>
          )}
          {!data ? <p className="text-white/45 italic mt-10">Chargement…</p> : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">{items.map((i) => <ItemCard key={i.id} i={i} />)}</div>
          )}
          <p className="text-xs text-white/45 mt-8 max-w-3xl" data-testid="loc-catalog-deposit-note">{DEPOSIT_NOTE}</p>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 lg:px-14 border-b border-[#DCB457]/15" id="demande" data-testid="loc-request-section">
        <div className="max-w-[1100px] mx-auto">
          <span className="eyebrow">Demande de location</span>
          <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3">Recevez votre <span className="italic gold-text">devis.</span></h2>
          <p className="text-white/60 mt-4 max-w-2xl">Même fonctionnement que nos prestations : devis, signature électronique, acompte de 30 %, contrat de location et dépôt de garantie par carte bancaire.</p>
          {data && <RentalRequestForm items={data.items} />}
        </div>
      </section>

      <section className="py-20 px-6 md:px-10 lg:px-14 max-w-[1100px] mx-auto" data-testid="loc-faq">
        <span className="eyebrow">Questions fréquentes</span>
        <h2 className="font-serif-luxe text-3xl md:text-4xl mt-3">Tout savoir sur la location.</h2>
        <div className="mt-8 space-y-4">
          {FAQ.map((f) => (
            <div key={f.q} className="card-luxe p-6"><h3 className="font-serif-luxe text-xl text-[#DCB457]">{f.q}</h3><p className="text-sm text-white/70 mt-3 leading-relaxed">{f.a}</p></div>
          ))}
        </div>
        <p className="text-sm text-white/55 mt-10">Vous préférez une prestation complète avec animateur ? Découvrez nos formules <Link to="/photobooth" className="text-[#DCB457] underline underline-offset-4">photobooth</Link>, le <Link to="/prestations/livre-d-or" className="text-[#DCB457] underline underline-offset-4">livre d'or</Link> et nos <Link to="/prestations" className="text-[#DCB457] underline underline-offset-4">prestations DJ</Link>.</p>
      </section>
      <CTAFooter />
    </>
  );
}
